import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/admin/Modal";
import { formatDateTime } from "../../components/consumption/formatters";
import { adminProblemText, adminReasonKey } from "../../components/shared/accessProblem";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import styles from "../../components/admin/Admin.module.css";
import {
  administerPersonAccess,
  confirmPersonAccessDelete,
  fetchFlaggedAccounts,
  type FlaggedAccount,
} from "../../api/providerAccess";

/**
 * The "Provider access" tab of the Providers screen (AIA-109, US-10): the accounts flagged for deletion at a provider that issues
 * keys, with Keep and Confirm delete. A flag only ever asks an administrator to decide; nothing is deleted until Confirm delete is
 * confirmed here. The Providers screen gates it on `providers.administer`.
 */
export function ProviderAccessContent(): React.JSX.Element {
  return <FlaggedSection />;
}

type LoadState = { kind: "loading" } | { kind: "loaded"; accounts: FlaggedAccount[] } | { kind: "error"; message: string };

function FlaggedSection(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<FlaggedAccount | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await fetchFlaggedAccounts();
    if (result.kind === "ok") {
      setState({ kind: "loaded", accounts: result.response });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.entitlement.denied.description") });
    } else if (result.kind === "notFound") {
      setState({ kind: "error", message: t("providerAccess.problems.error") });
    } else {
      setState({ kind: "error", message: adminProblemText(result, t) });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const keep = async (account: FlaggedAccount): Promise<void> => {
    setBusyKey(rowKey(account));
    setFeedback(null);
    const result = await administerPersonAccess(account.personId, account.vendorId, "keep");
    setBusyKey(null);
    if (result.kind === "ok") {
      await load();
    } else if (result.kind === "notFound") {
      setFeedback(t("providerAccess.problems.error"));
    } else {
      setFeedback(adminProblemText(result, t));
    }
  };

  if (state.kind === "loading") {
    return <LoadingState label={t("providerAccess.flagged.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("providerAccess.flagged.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <section className={styles.section} aria-labelledby="flagged-heading">
      <h2 id="flagged-heading">{t("providerAccess.flagged.title")}</h2>
      <p className={styles.note}>{t("providerAccess.flagged.intro")}</p>
      {feedback && (
        <p role="alert" className={`${styles.feedback} ${styles.feedbackError}`}>
          {feedback}
        </p>
      )}
      {state.accounts.length === 0 ? (
        <EmptyState title={t("providerAccess.flagged.emptyTitle")} description={t("providerAccess.flagged.emptyDescription")} />
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <caption>{t("providerAccess.flagged.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("providerAccess.flagged.person")}</th>
                <th scope="col">{t("providerAccess.flagged.vendor")}</th>
                <th scope="col">{t("providerAccess.flagged.flaggedAt")}</th>
                <th scope="col">{t("providerAccess.flagged.reason")}</th>
                <th scope="col">{t("providerAccess.flagged.keys")}</th>
                <th scope="col">{t("providerAccess.flagged.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {state.accounts.map((account) => (
                <tr key={rowKey(account)}>
                  <td>{account.personName !== "" ? account.personName : account.personId}</td>
                  <td>{t(`providerAccess.vendors.${account.vendorId}`, { defaultValue: account.vendorId })}</td>
                  <td>{formatDateTime(account.flaggedAt)}</td>
                  <td>{account.reason ? t(adminReasonKey(account.reason)) : "—"}</td>
                  <td>{account.credentialIds.length > 0 ? account.credentialIds.join(", ") : "—"}</td>
                  <td>
                    <div className={styles.inlineControls}>
                      <button type="button" className="button button-secondary" disabled={busyKey === rowKey(account)} onClick={() => void keep(account)}>
                        {t("providerAccess.flagged.keep")}
                      </button>
                      <button type="button" className="button button-primary" disabled={busyKey === rowKey(account)} onClick={() => setDeleting(account)}>
                        {t("providerAccess.flagged.confirmDelete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {deleting && (
        <ConfirmDeleteDialog
          account={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}
    </section>
  );
}

function rowKey(account: FlaggedAccount): string {
  return `${account.personId}:${account.vendorId}`;
}

type Outcome = { kind: "dryRun" } | { kind: "stillEnabled" } | { kind: "error"; message: string };

function ConfirmDeleteDialog({ account, onClose, onDeleted }: { account: FlaggedAccount; onClose: () => void; onDeleted: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const person = account.personName !== "" ? account.personName : account.personId;
  const vendor = t(`providerAccess.vendors.${account.vendorId}`, { defaultValue: account.vendorId });

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setOutcome(null);
    const result = await confirmPersonAccessDelete(account.personId, account.vendorId);
    setBusy(false);
    if (result.kind === "ok") {
      if (result.response?.dryRun) {
        setOutcome({ kind: "dryRun" });
      } else {
        onDeleted();
      }
    } else if (result.kind === "conflict" && result.reason === "ProviderStillEnabled") {
      setOutcome({ kind: "stillEnabled" });
    } else if (result.kind === "notFound") {
      setOutcome({ kind: "error", message: t("providerAccess.problems.error") });
    } else {
      setOutcome({ kind: "error", message: adminProblemText(result, t) });
    }
  };

  return (
    <Modal title={t("providerAccess.flagged.dialogTitle")} onClose={onClose}>
      <p>{t("providerAccess.flagged.dialogBody", { person, vendor })}</p>
      <p>
        {t("providerAccess.flagged.dialogKeys")}: {account.credentialIds.length > 0 ? account.credentialIds.join(", ") : "—"}
      </p>
      {outcome?.kind === "dryRun" && <output className={styles.feedback}>{t("providerAccess.flagged.dryRun")}</output>}
      {outcome?.kind === "stillEnabled" && (
        <p role="alert" className={`${styles.feedback} ${styles.feedbackError}`}>
          {t("providerAccess.flagged.stillEnabled", { vendor })}
        </p>
      )}
      {outcome?.kind === "error" && (
        <p role="alert" className={`${styles.feedback} ${styles.feedbackError}`}>
          {outcome.message}
        </p>
      )}
      <div className={styles.inlineControls}>
        <button type="button" className="button button-primary" disabled={busy} onClick={() => void confirm()}>
          {t("providerAccess.flagged.dialogConfirm")}
        </button>
        {/* Initial focus goes to Cancel, not to the destructive action, so a stray Enter cannot delete an account. */}
        <button type="button" className="button button-secondary" data-autofocus onClick={onClose}>
          {t("providerAccess.flagged.dialogCancel")}
        </button>
      </div>
    </Modal>
  );
}
